import React from "react";
import { Layers, Zap } from "lucide-react";
import CrewBoostButton from "../CrewBoostButton";

/**
 * ConsoleScenesRail — top section of the DirectorConsole left rail.
 * Renders the Crew Boost launcher + the 2-col Scenes grid (with
 * optional media poster + "set media" edit affordance).
 *
 * Extracted from DirectorConsole.jsx to keep that page skimmable.
 * All of the interaction state (active scene, attached media, media
 * picker) still lives on the page — the rail is pure presentation.
 *
 * @param {Object}   props
 * @param {string}   props.streamId    For Crew Boost analytics scoping.
 * @param {Array}    props.scenes      Config — `[{id, label}]`. Sourced from SCENES const.
 * @param {string}   props.activeId    Currently-live scene id.
 * @param {Object}   props.sceneMedia  Map of `sceneId -> {url, label}`.
 * @param {Fn}       props.onSwitch    (sceneId) => void — program-switch.
 * @param {Fn}       props.onEditMedia ({target,label,initial}) => void — open picker.
 */
export default function ConsoleScenesRail({
  streamId,
  scenes,
  activeId,
  sceneMedia,
  onSwitch,
  onEditMedia,
}) {
  return (
    <>
      {/* Crew Boost — sits above Scenes per host layout request. It's
          intentionally OFF the live stream (no on-screen surge chip). */}
      <div
        data-testid="left-crew-boost"
        className="mb-3 bg-gradient-to-br from-orange-500/15 to-tv-live/10 border border-orange-400/30 rounded-lg p-2.5"
      >
        <div className="text-[10px] uppercase tracking-wider font-black text-orange-300 mb-2 flex items-center gap-1">
          <Zap className="w-3 h-3" /> Crew Boost
        </div>
        <CrewBoostButton streamId={streamId} compact />
        <div className="text-[9px] text-white/40 italic mt-1.5">
          Fire or schedule an AI crowd surge
        </div>
      </div>

      <div className="flex items-center gap-2 text-white/60 font-display font-bold text-xs uppercase tracking-wider mb-3">
        <Layers className="w-4 h-4 text-tv-accent" /> Scenes
      </div>
      <div className="grid grid-cols-2 gap-2">
        {scenes.map((s) => {
          const active = activeId === s.id;
          const media = sceneMedia[s.id];
          return (
            <div key={s.id} className="relative group">
              <button
                data-testid={`console-scene-${s.id}`}
                onClick={() => onSwitch(s.id)}
                className={`aspect-video w-full rounded-lg text-[10px] font-black uppercase tracking-wider transition overflow-hidden relative border ${
                  active
                    ? "border-tv-live bg-tv-live/20 text-white shadow-[0_0_0_2px_rgba(255,0,64,0.45)_inset]"
                    : "border-white/10 bg-white/5 text-white/70 hover:bg-white/10"
                }`}
                title={media ? `${s.label} — ${media.label || "Media attached"}` : s.label}
              >
                {/* If media is attached, show a muted poster so the tile previews the content */}
                {media?.url && (
                  <video
                    src={media.url}
                    muted
                    playsInline
                    preload="metadata"
                    className="absolute inset-0 w-full h-full object-cover opacity-60"
                  />
                )}
                <span className="relative z-10 absolute inset-x-0 bottom-1 text-center drop-shadow">
                  {s.label.split(" ")[0]}
                </span>
                {active && (
                  <span className="absolute top-1 left-1 bg-tv-live text-white text-[7px] font-black px-1 rounded z-10">
                    PGM
                  </span>
                )}
                {media && (
                  <span
                    className="absolute top-1 right-1 bg-tv-accent text-black text-[7px] font-black px-1 rounded z-10"
                    title="Media attached"
                  >
                    ▶
                  </span>
                )}
              </button>
              {/* Edit-media affordance — positioned bottom-right, visible
                  on hover so scene tiles stay clean when idle. */}
              <button
                data-testid={`console-scene-media-${s.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onEditMedia({ target: `scene:${s.id}`, label: s.label, initial: media || null });
                }}
                className="absolute bottom-1 right-1 w-5 h-5 rounded-md bg-black/70 hover:bg-tv-accent hover:text-black text-white/80 border border-white/15 flex items-center justify-center text-[10px] font-black z-20 opacity-0 group-hover:opacity-100 transition"
                title="Set scene media"
              >
                +
              </button>
            </div>
          );
        })}
      </div>
    </>
  );
}
